import { Link } from "react-router-dom";

const CheckIcon = () => (
  <svg
    className="w-5 h-5 text-green-500 flex-shrink-0"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export const ExpatHorizonPage = () => {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <span className="bg-white/10 border border-white/20 text-white px-3 py-1.5 rounded-full text-sm font-medium">
                  Cross-Border Financial Planning
                </span>
                <span className="bg-green-500/20 border border-green-400/30 text-green-300 px-3 py-1.5 rounded-full text-sm font-semibold">
                  Now Live
                </span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Expat <span className="text-orange-300">Horizon</span>
              </h1>

              <p className="text-xl text-blue-100 mb-4 leading-relaxed">
                The lifetime financial planning tool built specifically for
                Indian professionals living abroad — across the US, Canada,
                India, and beyond.
              </p>
              <p className="text-lg text-blue-200 mb-8">
                Model retirement scenarios with Monte Carlo simulations,
                optimize cross-border withdrawals, and stay ahead of FBAR,
                FATCA, and DTAA obligations — all in one place.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://expat-horizon.avtechai.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-orange-500 text-white px-8 py-3 rounded-lg font-bold hover:bg-orange-400 transition-colors inline-flex items-center justify-center"
                >
                  Launch App
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
                <a
                  href="#features"
                  className="border-2 border-white/40 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center"
                >
                  See Features
                </a>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <h3 className="text-lg font-semibold text-white mb-6">
                Built for the global Indian professional
              </h3>
              <div className="space-y-4">
                {[
                  {
                    flag: "🇺🇸",
                    country: "United States",
                    note: "401(k), IRA, brokerage, FBAR",
                  },
                  {
                    flag: "🇨🇦",
                    country: "Canada",
                    note: "RRSP, TFSA, CPP, NRO/NRE",
                  },
                  {
                    flag: "🇮🇳",
                    country: "India",
                    note: "PPF, NPS, real estate, DTAA",
                  },
                ].map(({ flag, country, note }) => (
                  <div
                    key={country}
                    className="flex items-center gap-4 bg-white/5 rounded-xl p-4"
                  >
                    <span className="text-3xl">{flag}</span>
                    <div>
                      <p className="font-semibold text-white">{country}</p>
                      <p className="text-blue-300 text-sm">{note}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 text-center">
                <p className="text-blue-300 text-sm">
                  Multi-currency, multi-jurisdiction — one unified view
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900">
              Who Expat Horizon Is For
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: "💼",
                title: "H1-B / L1 Visa Professionals",
                desc: "Working in the US, building wealth across two countries — with retirement accounts in both.",
              },
              {
                icon: "🏡",
                title: "Permanent Residents & Citizens",
                desc: "Settled in the US or Canada with family assets in India: property, PPF, NPS, and NRE/NRO accounts.",
              },
              {
                icon: "✈️",
                title: "Returning Expats",
                desc: "Planning to move back to India — need to model optimal withdrawal sequences across multiple currencies.",
              },
            ].map(({ icon, title, desc }) => (
              <div
                key={title}
                className="bg-white rounded-xl p-6 shadow-sm border border-amber-100"
              >
                <div className="text-4xl mb-4">{icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Expat Horizon Does
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A complete financial planning engine that understands the
              complexities of living and retiring across borders.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Monte Carlo Projections */}
            <div className="flex gap-6">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-7 h-7 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Monte Carlo Retirement Projections
                </h3>
                <p className="text-gray-600 mb-4">
                  Run thousands of simulations to model your retirement
                  probability across different market scenarios, inflation
                  rates, and withdrawal strategies.
                </p>
                <ul className="space-y-2">
                  {[
                    "Probability of portfolio survival to target age",
                    "Inflation-adjusted projections per country",
                    "Best/worst/median scenario bands",
                    "Withdrawal rate sensitivity analysis",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-gray-600"
                    >
                      <CheckIcon />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Cross-Border Optimization */}
            <div className="flex gap-6">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-7 h-7 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Cross-Border Withdrawal Optimization
                </h3>
                <p className="text-gray-600 mb-4">
                  Determine the optimal sequence and amounts to withdraw from
                  US, Canadian, and Indian accounts — minimizing lifetime tax
                  across all jurisdictions.
                </p>
                <ul className="space-y-2">
                  {[
                    "401(k) vs IRA vs taxable account sequencing",
                    "NRE/NRO repatriation modeling",
                    "DTAA treaty benefit application",
                    "Currency conversion impact analysis",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-gray-600"
                    >
                      <CheckIcon />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Tax Compliance */}
            <div className="flex gap-6">
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-7 h-7 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Tax Compliance Awareness
                </h3>
                <p className="text-gray-600 mb-4">
                  Stay informed about cross-border tax obligations. Expat
                  Horizon surfaces FBAR/FATCA thresholds and DTAA implications
                  as you model your finances.
                </p>
                <ul className="space-y-2">
                  {[
                    "FBAR filing threshold tracking ($10K foreign accounts)",
                    "FATCA Form 8938 awareness",
                    "India-US DTAA double-tax relief modeling",
                    "NRI/RNOR/Resident status impact calculator",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-gray-600"
                    >
                      <CheckIcon />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Asset Management */}
            <div className="flex gap-6">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-7 h-7 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Multi-Country Asset Tracking
                </h3>
                <p className="text-gray-600 mb-4">
                  One consolidated view of all your accounts — US brokerage,
                  Indian real estate, retirement accounts, and cash — across
                  currencies.
                </p>
                <ul className="space-y-2">
                  {[
                    "US: 401(k), IRA, brokerage, HSA",
                    "India: PPF, NPS, real estate, FD, EPF",
                    "Canada: RRSP, TFSA, non-reg accounts",
                    "Household shared access for couples",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-gray-600"
                    >
                      <CheckIcon />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Household Feature */}
      <section className="py-16 bg-gradient-to-r from-slate-700 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Built for Families, Not Just Individuals
              </h3>
              <p className="text-blue-200 text-lg mb-6">
                Expat Horizon uses a household model — both spouses share one
                financial picture. Plan joint retirement, model single-income
                scenarios, and track each member's accounts separately while
                seeing the combined view.
              </p>
              <ul className="space-y-3 text-blue-100">
                {[
                  "Invite spouse/partner — shared household view",
                  "Per-member income, account, and profile tracking",
                  "Joint Monte Carlo simulation across both portfolios",
                  "Survivor scenario modeling",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-orange-300 rounded-full flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="w-16 h-16 bg-blue-500/30 rounded-full flex items-center justify-center text-2xl">
                  👤
                </div>
                <div className="text-white/40 text-2xl font-light">+</div>
                <div className="w-16 h-16 bg-orange-500/30 rounded-full flex items-center justify-center text-2xl">
                  👤
                </div>
              </div>
              <p className="text-white font-semibold text-lg">
                Household Account
              </p>
              <p className="text-blue-300 text-sm mt-2">
                One plan, two perspectives
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center bg-green-500/20 text-green-300 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Now Live
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Start Planning Your Expat Horizon
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Model your cross-border retirement, optimize withdrawals, and stay
            on top of your tax obligations — free to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://expat-horizon.avtechai.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-orange-400 transition-colors inline-flex items-center justify-center"
            >
              Launch App
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
            <Link
              to="/products"
              className="border-2 border-gray-500 text-gray-300 px-10 py-4 rounded-lg font-bold text-lg hover:border-white hover:text-white transition-colors inline-flex items-center justify-center"
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
